/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import _ from 'underscore';
import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState
} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { anime } from 'react-anime';

import { WrapContext } from 'contexts/WrapContext';

const PositionedBlock = ({
	disableBackdrop,
	anchorRef,
	children,
	align,
	offset,
	onRequestClose
}) => {
	const elementRef = useRef(null);
	const [wrapContext, setWrapContext] = useContext(WrapContext);

	const getOffsetObject = useCallback(
		(element, anchorEl) => {
			const { left, top, height, right } = anchorEl.getBoundingClientRect();
			const scrollTop = window.pageYOffset;
			const elementWidth = element ? element.offsetWidth : 0;

			let offsetLeft = align === 'right' ? right - elementWidth : left;
			const distance =
				document.body.clientWidth - (offsetLeft + elementWidth + 20);

			if (distance < 0) {
				offsetLeft += distance;
			}
			if (offsetLeft < 0) {
				offsetLeft = 10;
			}

			return {
				offsetTop: top + scrollTop,
				offsetLeft,
				offsetHeight: height
			};
		},
		[align]
	);

	const [elementOffset, setElementOffset] = useState(
		getOffsetObject(elementRef.current, anchorRef.current)
	);

	const scrollPage = useCallback((element) => {
		let scrollTop;
		const visualViewportTop = window.scrollY || window.pageYOffset;

		if (window.innerHeight > element.offsetHeight + 40) {
			const diffTop = element.offsetTop - visualViewportTop;
			const diffBottom = diffTop + element.offsetHeight - window.innerHeight;

			if (diffTop < 0) {
				scrollTop = visualViewportTop + diffTop - 20;
			} else if (diffBottom > 0) {
				scrollTop = visualViewportTop + diffBottom + 20;
			}
		} else {
			scrollTop = elementOffset.offsetTop;
		}

		if (scrollTop) {
			const scrollElement =
				window.document.scrollingElement ||
				window.document.body ||
				window.document.documentElement;

			anime({
				targets: scrollElement,
				scrollTop,
				duration: 500,
				easing: 'easeInOutQuad'
			});
		}
	}, []);

	const changePosition = useCallback(() => {
		setElementOffset(getOffsetObject(elementRef.current, anchorRef.current));

		if (elementRef.current) {
			scrollPage(elementRef.current);
		}
	}, [elementRef.current, anchorRef.current]);

	useEffect(() => {
		if (disableBackdrop) {
			setWrapContext({
				onClick: onRequestClose,
				modifiers: _(wrapContext.modifiers).union(['blur'])
			});
		}

		scrollPage(elementRef.current);

		const throttledChangePosition = _.throttle(changePosition, 500);

		window.addEventListener('resize', throttledChangePosition);

		return () => {
			if (disableBackdrop) {
				setWrapContext({
					onClick: _.noop,
					modifiers: _(wrapContext.modifiers).without('blur')
				});
			}

			window.removeEventListener('resize', throttledChangePosition);
		};
	}, [onRequestClose, disableBackdrop, changePosition]);

	useEffect(() => {
		setElementOffset(getOffsetObject(elementRef.current, anchorRef.current));
	}, [elementRef.current, anchorRef.current]);

	const onBlockClick = useCallback((event) => event.stopPropagation(), []);

	const { offsetTop, offsetLeft, offsetHeight } = elementOffset;
	const top = offsetTop + offsetHeight + offset;
	const style = {
		top,
		left: offsetLeft,
		position: 'absolute',
		zIndex: '10'
	};

	return ReactDOM.createPortal(
		<div ref={elementRef} style={style} onClick={onBlockClick}>
			{children}
		</div>,
		document.body
	);
};

PositionedBlock.propTypes = {
	disableBackdrop: PropTypes.bool,
	anchorRef: PropTypes.object.isRequired,
	align: PropTypes.oneOf(['right', 'left']),
	children: PropTypes.node,
	offset: PropTypes.number,
	onRequestClose: PropTypes.func
};

PositionedBlock.defaultProps = {
	disableBackdrop: true,
	align: 'left',
	offset: 0
};

export default PositionedBlock;
